/**
 * The error codes the media surface can answer with (U3).
 *
 * They were api-local string literals while only the api read them. P15a is
 * where that stops being survivable: a reviewer opening an ID document has to be
 * told *which* refusal they hit, because the three that reach that screen mean
 * genuinely different things and lead to different next actions —
 *
 * - `media_not_found` — the evidence is **gone**. A retention purge fired, or
 *   the member deleted it. Nothing to retry; the case has to be decided without
 *   it, or sent back.
 * - `media_forbidden` — this operator lacks `verification.review`. Actionable,
 *   and deliberately not the same answer as "not found" (a 403 on somebody
 *   else's asset id would hand a *member* an enumeration oracle, which is why
 *   the api chooses between the two by role — `MediaAccessService`).
 * - `media_not_ready` — the upload never finished. The asset exists and simply
 *   has no content the platform vouches for yet.
 *
 * Two hand-typed copies of a string that decides what an operator is shown is
 * exactly the drift U3 exists to prevent, so every code is listed here and the
 * api's `DomainError` subclasses read from this object. One place a code is
 * spelled — the same shape `ACCESS_ERROR_CODES` took for the same reason.
 */
export const MEDIA_ERROR_CODES = {
  /** No such asset, or it is tombstoned. Also the refusal a non-owner member gets. */
  notFound: 'media_not_found',
  /** Staff without the kind's `staffPermission`. */
  forbidden: 'media_forbidden',
  /**
   * Staff who hold the permission but may not review **this member**.
   *
   * `verification.genderMatchedReview` is on and the caller's gender differs from
   * the owner's, on a kind declared `isGenderMatchedStaffRead` (P15b).
   *
   * A code of its own rather than reusing {@link forbidden}, because the two lead
   * a reviewer somewhere different: the copy behind `forbidden` tells them to ask
   * an administrator for `verification.review` — advice that is simply wrong
   * here, where they already hold it and no permission grant will ever change the
   * answer. Four ways to have no picture were already four conversations (P15a);
   * this is the fifth.
   */
  genderMismatch: 'media_gender_mismatch',
  /**
   * A member reached another member's photo whose owner blurs until match, and
   * these two are not matched (P17a).
   *
   * **403, and its own code**, for the reason {@link genderMismatch} is: the copy
   * behind {@link forbidden} tells the caller to ask an administrator for a
   * permission, which is nonsense advice to a member — they hold none, and none
   * would change this answer. The only route forward is a mutual like, and this
   * is the one refusal in the platform whose honest next action is "she has to
   * like you back".
   *
   * It is deliberately **not** a 404. Nothing is disclosed by it that the member
   * did not already have: they are looking at a profile they were legitimately
   * shown, whose payload already told them the photos are blurred and handed them
   * the `blurHash` to render. Collapsing it into "not found" would make the app
   * unable to tell "blurred" from "deleted" and would put a broken-image state on
   * a screen that has a correct one.
   */
  blurredUntilMatch: 'media_blurred_until_match',
  /** The asset exists but is not `ready` — the upload never finished. */
  notReady: 'media_not_ready',

  /** Upload-time refusals, before any bytes are accepted. */
  mimeNotAllowed: 'media_mime_not_allowed',
  tooLarge: 'media_too_large',
  kindNotUploadableHere: 'media_kind_not_uploadable_here',
  sortOrderNotSupported: 'media_sort_order_not_supported',
  quotaExceeded: 'media_quota_exceeded',

  /** Finalize-time refusals, once the object is meant to be in the bucket. */
  objectMissing: 'media_object_missing',
  objectMismatch: 'media_object_mismatch',
  notAnImage: 'media_not_an_image',
  /** Declared as a document, but the bytes carry no document header (F1). */
  notADocument: 'media_not_a_document',
  alreadyFinalized: 'media_already_finalized',
  /**
   * The kind is not one its owner may delete (`ownerDeletable: false`) — a
   * payment receipt, which is the platform's proof of a payment as much as the
   * member's. 409: the asset is theirs (so not a 404) and the answer will not
   * change on a retry.
   */
  notOwnerDeletable: 'media_not_owner_deletable',
} as const;

export type MediaErrorCode = (typeof MEDIA_ERROR_CODES)[keyof typeof MEDIA_ERROR_CODES];
